"use client";

import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { motion } from "motion/react";
import { format } from "date-fns";
import { Icon } from "@iconify-icon/react";
import { TooltipProvider } from "@radix-ui/react-tooltip";

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Spinner } from "@/components/ui/spinner";
import { Skeleton } from "@/components/ui/skeleton";
import { CoachMarker } from "@/components/CoachMarker";
import { currentLanguageLocale } from "@/i18n/getDateFnsLocale";
import { axiosApiInstance } from "@/libs/axios";
import { API_ENDPOINTS } from "@/constants/api";
import { toast } from "@/utils/customToast.tsx";
import { NewInvitationDialog } from "@/features/workspaces/tutor/components/workspace/NewInvitationDialog.tsx";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";

interface Invitation {
  id: string;
  type: "ONE_TIME" | "UNLIMITED" | "EMAIL";
  description?: string;
  created_at: string;
  active: boolean;
  invitees: Array<{
    id: string;
    first_name: string;
    last_name: string;
    avatar_path?: string;
  }>;
}

interface InvitationsResponse {
  data: Invitation[];
  meta: {
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
  };
}

const InvitationsPage = () => {
  const { t } = useTranslation(["workspaces", "common", "help"]);
  const [invitations, setInvitations] = useState<InvitationsResponse | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isDeletingId, setIsDeletingId] = useState<string | null>(null);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);

  const fetchInvitations = async (page = 1) => {
    if (!isLoading) setIsLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams({
        page: page.toString(),
        per_page: "20",
      });

      const { data } = await axiosApiInstance.get<InvitationsResponse>(
        `${API_ENDPOINTS.WORKSPACES.INVITATIONS.INVITATIONS}?${params.toString()}`,
      );

      setInvitations(data);
    } catch (err) {
      console.error("Error fetching invitations:", err);
      setError("Failed to load invitations");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDisableInvitation = async (invitationId: string) => {
    setIsDeletingId(invitationId);
    try {
      await axiosApiInstance.delete(
        `${API_ENDPOINTS.WORKSPACES.INVITATIONS.INVITATIONS}/${invitationId}/disable`,
      );

      // @ts-ignore
      toast.success(t("invitationDisabledSuccess"));
      fetchInvitations(currentPage);
    } catch (error) {
      console.error("Error disabling invitation:", error);
      // @ts-ignore
      toast.error(t("invitationDisableError"));
    } finally {
      setIsDeletingId(null);
    }
  };

  const handleCopyInvitationLink = (invitationLink: string) => {
    navigator.clipboard.writeText(invitationLink);
    // @ts-ignore
    toast.success(t("invitationLinkCopied"));
  };

  useEffect(() => {
    fetchInvitations(currentPage);
  }, [currentPage]);

  // Render loading skeleton
  if (isLoading && !invitations) {
    return (
      <div className="bg-background min-h-[calc(100dvh_-_4rem)]">
        <ScrollArea className="h-[calc(100dvh_-_4rem)]">
          <div className="mx-auto max-w-6xl p-4 md:p-6">
            {/* Hero Header Skeleton */}
            <div className="bg-side-accent/50 relative mb-6 overflow-visible rounded-2xl border-none p-4 md:p-8">
              <div className="relative">
                <div className="mb-6 flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
                  <div className="flex items-center gap-4 md:gap-6">
                    <Skeleton className="h-12 w-12 rounded-2xl md:h-16 md:w-16" />
                    <div>
                      <Skeleton className="mb-2 h-6 w-32 md:h-8 md:w-48" />
                      <Skeleton className="h-4 w-48 md:h-5 md:w-64" />
                    </div>
                  </div>
                  <Skeleton className="h-10 w-full md:w-40" />
                </div>
              </div>
            </div>
            {/* Invitation Cards Skeleton */}
            <div className="space-y-3 md:space-y-4">
              {[1, 2, 3, 4].map((i) => (
                <Skeleton key={i} className="h-20 w-full rounded-2xl md:h-24" />
              ))}
            </div>
          </div>
        </ScrollArea>
      </div>
    );
  }

  // Render error state
  if (error) {
    return (
      <div className="bg-background min-h-[calc(100dvh_-_4rem)]">
        <ScrollArea className="h-[calc(100dvh_-_4rem)]">
          <div className="mx-auto max-w-6xl p-4 md:p-6">
            <motion.div
              animate={{ opacity: 1, scale: 1 }}
              className="bg-foreground/50 flex h-80 flex-col items-center justify-center rounded-2xl backdrop-blur-xl md:h-96"
              initial={{ opacity: 0, scale: 0.9 }}
              transition={{ duration: 0.4 }}
            >
              <div className="bg-muted/20 mb-6 flex h-16 w-16 items-center justify-center rounded-2xl md:h-20 md:w-20">
                <Icon
                  className="text-muted-foreground"
                  height={"2rem"}
                  icon="tabler:alert-triangle"
                  width={"2rem"}
                />
              </div>
              <h2 className="text-accent-foreground mb-2 text-lg font-semibold md:text-xl">
                {t("common:errors.genericErrorTitle")}
              </h2>
              <p className="text-muted-foreground mb-6 text-center text-sm md:text-base">{error}</p>
              <Button onClick={() => fetchInvitations(currentPage)}>{t("common:retry")}</Button>
            </motion.div>
          </div>
        </ScrollArea>
      </div>
    );
  }

  return (
    <div className="bg-background min-h-[calc(100dvh_-_4rem)]">
      <ScrollArea className="h-[calc(100dvh_-_4rem)]">
        <div className="mx-auto max-w-6xl p-4 md:p-6">
          <title>{`${t("workspaces:invitations")} - Ximly`}</title>

          {/* Hero Header */}
          <CoachMarker
            description={t("help:coachMarkers.invitationsOverview.description", {
              defaultValue:
                "Manage your workspace invitations here. You can create new invitations, view existing ones, and track who has joined your workspaces through invitation links.",
            })}
            id="invitations-overview"
            placement="top"
            title={t("help:coachMarkers.invitationsOverview.title", {
              defaultValue: "Workspace Invitations",
            })}
          >
            <motion.div
              animate={{ opacity: 1, y: 0 }}
              className="bg-side-accent/50 text-accent-foreground relative mb-6 rounded-2xl border-none p-4 shadow-2xl md:mb-8 md:p-8"
              initial={{ opacity: 0, y: 20 }}
              transition={{ duration: 0.4 }}
            >
              {/* Background Pattern */}
              <div className="absolute inset-0 overflow-hidden rounded-2xl">
                <div className="absolute -top-10 -left-10 h-24 w-24 rounded-full bg-gradient-to-br from-violet-400 to-purple-600 opacity-5 md:-top-20 md:-left-20 md:h-40 md:w-40" />
                <div className="absolute -right-8 -bottom-8 h-20 w-20 rounded-full bg-gradient-to-tr from-pink-400 to-rose-500 opacity-5 md:-right-16 md:-bottom-16 md:h-32 md:w-32" />
              </div>

              <div className="relative">
                <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
                  <div className="flex items-center gap-4 md:gap-6">
                    <motion.div
                      animate={{ scale: 1 }}
                      className="bg-muted/20 flex items-center justify-center rounded-2xl p-2 backdrop-blur-sm md:p-4"
                      initial={{ scale: 0.8 }}
                      transition={{ duration: 0.3, delay: 0.2 }}
                    >
                      <Icon
                        className="text-accent-foreground"
                        height="1.5rem"
                        icon="mdi:email-open"
                        width="1.5rem"
                      />
                    </motion.div>
                    <div>
                      <h1 className="text-accent-foreground text-2xl leading-tight font-bold md:text-3xl">
                        {t("workspaces:invitations")}
                      </h1>
                      <p className="text-muted-foreground mt-1 text-sm md:mt-2 md:text-base">
                        {t("workspaces:inviteMembersDescription")}
                      </p>
                    </div>
                  </div>

                  {/* Create Button */}
                  <Button
                    className="bg-accent-foreground/90 hover:bg-accent-foreground text-primary my-auto"
                    size={"lg"}
                    onClick={() => setIsCreateDialogOpen(true)}
                  >
                    <Icon height={"1rem"} icon="tabler:plus" width={"1rem"} />
                    {t("workspaces:createInvitation")}
                  </Button>
                </div>
              </div>
            </motion.div>
          </CoachMarker>

          {/* Invitations List */}
          {isLoading ? (
            <div className="relative min-h-[400px]">
              <div className="flex h-96 items-center justify-center">
                <Spinner className="h-8 w-8" />
              </div>
            </div>
          ) : invitations && invitations.data.length === 0 ? (
            <motion.div
              animate={{ opacity: 1, scale: 1 }}
              className="bg-foreground/50 relative rounded-2xl border-none p-8 text-center shadow-2xl backdrop-blur-xl md:p-12"
              initial={{ opacity: 0, scale: 0.9 }}
              transition={{ duration: 0.4 }}
            >
              {/* Background Pattern */}
              <div className="absolute inset-0 overflow-hidden rounded-2xl">
                <div className="absolute -top-8 -right-8 h-20 w-20 rounded-full bg-gradient-to-br from-emerald-400 to-teal-600 opacity-5 md:-top-16 md:-right-16 md:h-32 md:w-32" />
                <div className="absolute -bottom-6 -left-6 h-16 w-16 rounded-full bg-gradient-to-tr from-blue-400 to-cyan-500 opacity-5 md:-bottom-12 md:-left-12 md:h-24 md:w-24" />
              </div>

              <div className="relative">
                <div className="bg-muted/20 mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-2xl backdrop-blur-sm md:h-20 md:w-20">
                  <Icon
                    className="text-muted-foreground"
                    height="2rem"
                    icon="mdi:email-outline"
                    width="2rem"
                  />
                </div>
                <h2 className="text-accent-foreground mb-3 text-xl font-semibold md:text-2xl">
                  {t("workspaces:noData.noInvitations")}
                </h2>
                <p className="text-muted-foreground mx-auto mb-6 max-w-md text-sm md:mb-8 md:text-base">
                  {t("workspaces:createNewWorkspace")}
                </p>
                <Button
                  className="bg-accent-foreground/90 hover:bg-accent-foreground text-primary"
                  size={"lg"}
                  onClick={() => setIsCreateDialogOpen(true)}
                >
                  <Icon height={"1rem"} icon="tabler:plus" width={"1rem"} />
                  {t("workspaces:createInvitation")}
                </Button>
              </div>
            </motion.div>
          ) : (
            <motion.div
              animate={{ opacity: 1, y: 0 }}
              initial={{ opacity: 0, y: 20 }}
              transition={{ duration: 0.4, delay: 0.1 }}
            >
              {/* Invitations Table */}
              <motion.div
                animate={{ opacity: 1, y: 0 }}
                className="bg-foreground/50 relative overflow-hidden rounded-2xl border-none shadow-2xl backdrop-blur-xl"
                initial={{ opacity: 0, y: 20 }}
                transition={{ duration: 0.4, delay: 0.1 }}
              >
                <div className="relative p-0">
                  <div className="divide-border/20 flex flex-col divide-y">
                    {invitations?.data.map((invitation, index) => (
                      <motion.div
                        key={invitation.id}
                        animate={{ opacity: 1, y: 0 }}
                        className="bg-side-accent/50 group hover:bg-side-accent/70 relative p-4 transition-all duration-200 md:p-6"
                        initial={{ opacity: 0, y: 20 }}
                        transition={{ delay: index * 0.05 }}
                      >
                        {/* Mobile Layout */}
                        <div className="block lg:hidden">
                          <div className="space-y-3">
                            {/* Top row - Status and Type */}
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <div
                                  className={cn(
                                    "h-2.5 w-2.5 rounded-full",
                                    invitation.active ? "bg-green-500" : "bg-gray-500",
                                  )}
                                />
                                <Badge
                                  className={cn(
                                    "rounded-full px-2.5 py-1 text-xs font-medium",
                                    invitation.active
                                      ? "border-green-500/30 bg-green-500/20 text-green-400"
                                      : "border-gray-500/30 bg-gray-500/20 text-gray-400",
                                  )}
                                  variant="outline"
                                >
                                  {invitation.active
                                    ? t("workspaces:invitationStatus.active")
                                    : t("workspaces:invitationStatus.disabled")}
                                </Badge>
                              </div>
                              <Badge
                                className={cn(
                                  "rounded-full px-2.5 py-1 text-xs font-medium",
                                  invitation.type === "UNLIMITED"
                                    ? "border-emerald-500/30 bg-emerald-500/20 text-emerald-400"
                                    : invitation.type === "EMAIL"
                                      ? "border-purple-500/30 bg-purple-500/20 text-purple-400"
                                      : "border-blue-500/30 bg-blue-500/20 text-blue-400",
                                )}
                                variant="outline"
                              >
                                {invitation.type === "UNLIMITED"
                                  ? t("workspaces:invitationType.unlimited")
                                  : invitation.type === "EMAIL"
                                    ? t("workspaces:invitationType.email" as any)
                                    : t("workspaces:invitationType.one_time")}
                              </Badge>
                            </div>

                            {/* Description */}
                            <div className="min-h-[1.25rem]">
                              {invitation.description ? (
                                <p className="text-accent-foreground/90 text-sm leading-relaxed">
                                  {invitation.description}
                                </p>
                              ) : (
                                <p className="text-muted-foreground text-sm italic">
                                  {t("workspaces:noData.description")}
                                </p>
                              )}
                            </div>

                            {/* Bottom row - Date, Participants, and Actions */}
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-4">
                                {/* Created date */}
                                <div className="flex items-center gap-1.5">
                                  <Icon
                                    className="text-muted-foreground"
                                    height="0.875rem"
                                    icon="lucide:calendar"
                                    width="0.875rem"
                                  />
                                  <span className="text-muted-foreground text-xs">
                                    {format(new Date(invitation.created_at), "dd MMM", {
                                      locale: currentLanguageLocale(),
                                    })}
                                  </span>
                                </div>

                                {/* Participants */}
                                <div className="flex items-center gap-1.5">
                                  <Icon
                                    className="text-muted-foreground"
                                    height="0.875rem"
                                    icon="lucide:users"
                                    width="0.875rem"
                                  />
                                  <div className="flex items-center">
                                    {invitation.invitees.length > 0 ? (
                                      <div className="flex -space-x-1">
                                        {invitation.invitees.slice(0, 2).map((invitee) => (
                                          <TooltipProvider key={invitee.id}>
                                            <Tooltip>
                                              <TooltipTrigger asChild>
                                                <div className="bg-muted ring-background relative h-5 w-5 rounded-full ring-1">
                                                  {invitee.avatar_path ? (
                                                    <img
                                                      alt={`${invitee.first_name} ${invitee.last_name}`}
                                                      className="h-full w-full rounded-full object-cover"
                                                      src={
                                                        invitee.avatar_path || "/placeholder.svg"
                                                      }
                                                    />
                                                  ) : (
                                                    <div className="bg-accent-foreground/10 flex h-full w-full items-center justify-center rounded-full text-xs font-medium">
                                                      {invitee.first_name[0]}
                                                      {invitee.last_name[0]}
                                                    </div>
                                                  )}
                                                </div>
                                              </TooltipTrigger>
                                              <TooltipContent side="top">
                                                <p className="text-xs">
                                                  {invitee.first_name} {invitee.last_name}
                                                </p>
                                              </TooltipContent>
                                            </Tooltip>
                                          </TooltipProvider>
                                        ))}
                                        {invitation.invitees.length > 2 && (
                                          <TooltipProvider>
                                            <Tooltip>
                                              <TooltipTrigger asChild>
                                                <div className="bg-muted ring-background relative flex h-5 w-5 items-center justify-center rounded-full text-xs font-medium ring-1">
                                                  +{invitation.invitees.length - 2}
                                                </div>
                                              </TooltipTrigger>
                                              <TooltipContent side="top">
                                                <p className="text-xs">
                                                  {t("workspaces:moreInvitees", {
                                                    count: invitation.invitees.length - 2,
                                                  })}
                                                </p>
                                              </TooltipContent>
                                            </Tooltip>
                                          </TooltipProvider>
                                        )}
                                      </div>
                                    ) : (
                                      <span className="text-muted-foreground text-xs">
                                        {t("workspaces:noData.invitees")}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>

                              {/* Actions */}
                              <div className="flex items-center gap-1">
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Button
                                        className="h-8 w-8 rounded-lg"
                                        size="icon"
                                        variant="ghost"
                                        onClick={() =>
                                          handleCopyInvitationLink(
                                            `${window.location.origin}/student/workspaces/join/${invitation.id}`,
                                          )
                                        }
                                      >
                                        <Icon className="h-3.5 w-3.5" icon="lucide:copy" />
                                      </Button>
                                    </TooltipTrigger>
                                    <TooltipContent side="left">
                                      <p>{t("workspaces:copyLink")}</p>
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                                {invitation.active && (
                                  <TooltipProvider>
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <Button
                                          className="h-8 w-8 rounded-lg text-red-200"
                                          disabled={isDeletingId === invitation.id}
                                          size="icon"
                                          variant="destructive"
                                          onClick={() => handleDisableInvitation(invitation.id)}
                                        >
                                          {isDeletingId === invitation.id ? (
                                            <Icon
                                              className="h-3.5 w-3.5 animate-spin"
                                              icon="lucide:loader-2"
                                            />
                                          ) : (
                                            <Icon className="h-3.5 w-3.5" icon="lucide:trash-2" />
                                          )}
                                        </Button>
                                      </TooltipTrigger>
                                      <TooltipContent side="left">
                                        <p>{t("workspaces:disableInvitation")}</p>
                                      </TooltipContent>
                                    </Tooltip>
                                  </TooltipProvider>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Desktop Layout */}
                        <div className="hidden lg:flex lg:items-center lg:justify-between">
                          {/* Left side - Status indicator */}
                          <div className="flex items-center gap-4">
                            {/* Status indicator */}
                            <div className="flex items-center gap-2">
                              <div
                                className={cn(
                                  "h-2 w-2 rounded-full",
                                  invitation.active ? "bg-green-500" : "bg-gray-500",
                                )}
                              />
                              <Badge
                                className={cn(
                                  "w-20 justify-center rounded-full px-3 py-1 text-xs",
                                  invitation.active
                                    ? "border-green-500/30 bg-green-500/20 text-green-400"
                                    : "border-gray-500/30 bg-gray-500/20 text-gray-400",
                                )}
                                variant="outline"
                              >
                                {invitation.active
                                  ? t("workspaces:invitationStatus.active")
                                  : t("workspaces:invitationStatus.disabled")}
                              </Badge>
                            </div>

                            {/* Type badge */}
                            <Badge
                              className={cn(
                                "w-24 justify-center rounded-full px-3 py-1 text-xs",
                                invitation.type === "UNLIMITED"
                                  ? "border-emerald-500/30 bg-emerald-500/20 text-emerald-400"
                                  : invitation.type === "EMAIL"
                                    ? "border-purple-500/30 bg-purple-500/20 text-purple-400"
                                    : "border-blue-500/30 bg-blue-500/20 text-blue-400",
                              )}
                              variant="outline"
                            >
                              {invitation.type === "UNLIMITED"
                                ? t("workspaces:invitationType.unlimited")
                                : invitation.type === "EMAIL"
                                  ? t("workspaces:invitationType.email" as any)
                                  : t("workspaces:invitationType.one_time")}
                            </Badge>

                            {/* Created date */}
                            <div className="flex w-32 items-center gap-2">
                              <Icon
                                className="text-muted-foreground h-4 w-4"
                                icon="lucide:calendar"
                              />
                              <span className="text-muted-foreground text-sm">
                                {format(new Date(invitation.created_at), "dd MMM yyyy", {
                                  locale: currentLanguageLocale(),
                                })}
                              </span>
                            </div>

                            {/* Participants */}
                            <div className="flex w-32 items-center gap-2">
                              <Icon className="text-muted-foreground h-4 w-4" icon="lucide:users" />
                              <div className="flex items-center gap-1">
                                {invitation.invitees.length > 0 ? (
                                  <>
                                    <div className="flex -space-x-2">
                                      {invitation.invitees.slice(0, 3).map((invitee) => (
                                        <TooltipProvider key={invitee.id}>
                                          <Tooltip>
                                            <TooltipTrigger asChild>
                                              <div className="bg-muted ring-background relative h-6 w-6 rounded-full ring-2">
                                                {invitee.avatar_path ? (
                                                  <img
                                                    alt={`${invitee.first_name} ${invitee.last_name}`}
                                                    className="h-full w-full rounded-full object-cover"
                                                    src={invitee.avatar_path || "/placeholder.svg"}
                                                  />
                                                ) : (
                                                  <div className="bg-accent-foreground/10 flex h-full w-full items-center justify-center rounded-full text-xs font-medium">
                                                    {invitee.first_name[0]}
                                                    {invitee.last_name[0]}
                                                  </div>
                                                )}
                                              </div>
                                            </TooltipTrigger>
                                            <TooltipContent side="right">
                                              <p>
                                                {invitee.first_name} {invitee.last_name}
                                              </p>
                                            </TooltipContent>
                                          </Tooltip>
                                        </TooltipProvider>
                                      ))}
                                      {invitation.invitees.length > 3 && (
                                        <TooltipProvider>
                                          <Tooltip>
                                            <TooltipTrigger asChild>
                                              <div className="bg-muted ring-background relative flex h-6 w-6 items-center justify-center rounded-full text-xs font-medium ring-2">
                                                +{invitation.invitees.length - 3}
                                              </div>
                                            </TooltipTrigger>
                                            <TooltipContent>
                                              <p>
                                                {t("workspaces:moreInvitees", {
                                                  count: invitation.invitees.length - 3,
                                                })}
                                              </p>
                                            </TooltipContent>
                                          </Tooltip>
                                        </TooltipProvider>
                                      )}
                                    </div>
                                  </>
                                ) : (
                                  <span className="text-muted-foreground text-sm">
                                    {t("workspaces:noData.invitees")}
                                  </span>
                                )}
                              </div>
                            </div>
                            {/* Description */}
                            {invitation.description ? (
                              <span className="text-accent-foreground/90 w-60 truncate text-sm">
                                {invitation.description}
                              </span>
                            ) : (
                              <span className="text-muted-foreground w-60 truncate text-sm">
                                {t("workspaces:noData.description")}
                              </span>
                            )}
                          </div>

                          {/* Right side - Actions */}
                          <div className="flex items-center gap-2">
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    className="h-8 w-8 rounded-lg"
                                    size="icon"
                                    variant="ghost"
                                    onClick={() =>
                                      handleCopyInvitationLink(
                                        `${window.location.origin}/student/workspaces/join/${invitation.id}`,
                                      )
                                    }
                                  >
                                    <Icon className="h-4 w-4" icon="lucide:copy" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent side="left">
                                  <p>{t("workspaces:copyLink")}</p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                            {invitation.active && (
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button
                                      className="h-8 w-8 rounded-lg text-red-200"
                                      disabled={isDeletingId === invitation.id}
                                      size="icon"
                                      variant="destructive"
                                      onClick={() => handleDisableInvitation(invitation.id)}
                                    >
                                      {isDeletingId === invitation.id ? (
                                        <Icon
                                          className="h-4 w-4 animate-spin"
                                          icon="lucide:loader-2"
                                        />
                                      ) : (
                                        <Icon className="h-4 w-4" icon="lucide:trash-2" />
                                      )}
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent side="left">
                                    <p>{t("workspaces:disableInvitation")}</p>
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            )}
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </div>
              </motion.div>

              {/* Pagination */}
              {invitations && invitations.meta.last_page > 1 && (
                <div className="flex items-center justify-center gap-4 py-8">
                  <div className="flex items-center gap-2">
                    {/* Previous Page */}
                    {invitations.meta.current_page > 1 && (
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              className="h-10 bg-transparent px-4 py-2"
                              variant="outline"
                              onClick={() => setCurrentPage(invitations.meta.current_page - 1)}
                            >
                              <Icon
                                className="sm:mr-2"
                                height="1rem"
                                icon="tabler:chevron-left"
                                width="1rem"
                              />
                              <span className={"max-sm:hidden"}>{t("common:previousPage")}</span>
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent side="left">
                            <p>{t("common:previousPage")}</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    )}

                    {/* Page Numbers */}
                    <div className="flex items-center gap-1">
                      {Array.from({ length: invitations.meta.last_page }, (_, i) => i + 1).map(
                        (page) => (
                          <Button
                            key={page}
                            className={cn(
                              "h-10 w-10 p-0",
                              page === invitations.meta.current_page &&
                                "bg-accent text-accent-foreground",
                            )}
                            variant={page === invitations.meta.current_page ? "default" : "outline"}
                            onClick={() => setCurrentPage(page)}
                          >
                            {page}
                          </Button>
                        ),
                      )}
                    </div>

                    {/* Next Page */}
                    {invitations.meta.current_page < invitations.meta.last_page && (
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              className="h-10 bg-transparent px-4 py-2"
                              variant="outline"
                              onClick={() => setCurrentPage(invitations.meta.current_page + 1)}
                            >
                              <span className={"max-sm:hidden"}>{t("common:nextPage")}</span>
                              <Icon
                                className="sm:ml-2"
                                height="1rem"
                                icon="tabler:chevron-right"
                                width="1rem"
                              />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent side="left">
                            <p>{t("common:nextPage")}</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    )}
                  </div>
                </div>
              )}
            </motion.div>
          )}
        </div>
      </ScrollArea>

      {/* Create Invitation Dialog */}
      <NewInvitationDialog
        isOpen={isCreateDialogOpen}
        onClose={() => setIsCreateDialogOpen(false)}
      />
    </div>
  );
};

export default InvitationsPage;
